import { LANG } from "@/types";
import { routes } from "@/consts";
import { getMyCompany, updateAuthUser } from "@/store/authSlice";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLocale } from "@/middleware";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { cn } from "@/lib/utils";
import { Avatar, Menu, MenuItem } from "@mui/material";
import { isNull } from "lodash";
import { useTranslate } from "@/locales";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { GoInfo } from "react-icons/go";
import { Button } from "@/app/global-components/button";
import { MdArrowBackIos, MdArrowForwardIos } from "react-icons/md";
import { LogOutIcon, User2Icon } from "lucide-react";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { FaCog } from "react-icons/fa";

function LeftSide({
  marketPlaceList,
  toggleOpenSideBar,
  openSideBar,
  navMenu,
}: {
  toggleOpenSideBar: () => void;
  marketPlaceList: {
    title: string;
    path: string;
    show: boolean;
    icon: JSX.Element;
    header: string;
  }[];
  openSideBar: boolean;
  navMenu: {
    title: string;
    path: string;
    show: boolean;
    icon: JSX.Element;
    header: string;
  }[];
}) {
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const { locale } = useLocale();
  const ClosedIcon = () => {
    return (
      <>
        {openSideBar && locale === LANG.AR && (
          <KeyboardDoubleArrowRightIcon
            onClick={toggleOpenSideBar}
            className="absolute md:block xl:hidden hidden left-3 top-4 text-2xl cursor-pointer text-gray-600 hover:text-purple-600 transition-colors duration-200"
          />
        )}
        {!openSideBar && locale === LANG.AR && (
          <KeyboardDoubleArrowLeftIcon
            onClick={toggleOpenSideBar}
            className="absolute md:block xl:hidden hidden left-4 top-4 text-2xl cursor-pointer text-gray-600 hover:text-purple-600 transition-colors duration-200"
          />
        )}
        {openSideBar && locale === LANG.EN && (
          <KeyboardDoubleArrowLeftIcon
            onClick={toggleOpenSideBar}
            className="absolute md:block xl:hidden hidden right-4 top-4 text-2xl cursor-pointer text-gray-600 hover:text-purple-600 transition-colors duration-200"
          />
        )}
        {!openSideBar && locale === LANG.EN && (
          <KeyboardDoubleArrowRightIcon
            onClick={toggleOpenSideBar}
            className="absolute md:block xl:hidden hidden right-3 top-4 text-2xl cursor-pointer text-gray-600 hover:text-purple-600 transition-colors duration-200"
          />
        )}
      </>
    );
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const { t } = useTranslate(locale);
  const handelLogout = () => {
    dispatch(updateAuthUser(null));
    dispatch(getMyCompany(null));
    localStorage.removeItem("tokens");
    localStorage.removeItem("userInfo");
    navigate(routes.home);
    setAnchorEl(null);
  };
  return (
    <div
      className={cn(
        "bg-gradient-to-b from-white via-gray-50 to-gray-100 md:flex flex-col z-30 justify-between hidden h-screen border ",
        openSideBar ? "md:min-w-72" : "md:w-20"
        // locale === LANG.AR ? "border-l-1" : "border-r-1"
      )}
      style={{
        overflow: "hidden",
        background:
          "linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)",
      }}
    >
      <div style={{ height: "100%", position: "relative" }}>
        <ClosedIcon />

        {/* Modern Logo Section */}
        <div
          className={"pt-8 pb-6 text-center flex justify-center items-center"}
        >
          <div
            className={cn("text-3xl flex font-bold tracking-wider", {
              "flex-row-reverse": locale == LANG.AR,
            })}
          >
            M
            <span className={cn(openSideBar ? "block" : "md:hidden")}>
              edex
            </span>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="px-4 space-y-2">
          {/* Main Navigation */}
          <div className="space-y-1 flex flex-col gap-1">
            {navMenu
              .filter((item) => item.show)
              .map((item) => (
                <Link to={item.path} key={item.path}>
                  <div
                    className={cn(
                      "flex items-center gap-3 cursor-pointer px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 group",
                      {
                        "bg-gradient-to-r from-purple-50 to-blue-50 !text-purple-700 transform scale-105":
                          location.pathname == `/${locale}${item.path}`,
                        "text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 hover:!text-purple-700":
                          location.pathname != `/${locale}${item.path}`,
                      }
                    )}
                  >
                    <div
                      className={cn("text-xl transition-all duration-200", {
                        "!text-purple-700":
                          location.pathname == `/${locale}${item.path}`,
                        "text-gray-500 group-hover:!text-purple-600":
                          location.pathname != `/${locale}${item.path}`,
                      })}
                    >
                      {item.icon}
                    </div>
                    <span
                      className={cn(
                        "transition-all duration-200",
                        openSideBar
                          ? "block opacity-100"
                          : "md:hidden opacity-0"
                      )}
                    >
                      {item.title}
                    </span>
                  </div>
                </Link>
              ))}
          </div>

          {/* Divider */}
          <div className="my-6">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* Marketplace Navigation */}
          {!!authUser && (
            <div className="space-y-1">
              {marketPlaceList
                .filter((item) => item.show)
                .map((item) => (
                  <Link to={item.path} key={item.path}>
                    <div
                      className={cn(
                        "flex items-center gap-3 cursor-pointer px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 group",
                        {
                          "bg-gradient-to-r from-blue-500 to-blue-600 text-white transform scale-105":
                            location.pathname == item.path,
                          "text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-700":
                            location.pathname != item.path,
                        }
                      )}
                    >
                      <div
                        className={cn("text-xl transition-all duration-200", {
                          "text-white": location.pathname == item.path,
                          "text-gray-500 group-hover:text-blue-600":
                            location.pathname != item.path,
                        })}
                      >
                        {item.icon}
                      </div>
                      <span
                        className={cn(
                          "transition-all duration-200",
                          openSideBar
                            ? "block opacity-100"
                            : "md:hidden opacity-0"
                        )}
                      >
                        {item.title}
                      </span>
                    </div>
                  </Link>
                ))}
            </div>
          )}

          {/* Settings & Help */}
          <div className="space-y-1 mt-6">
            {!isNull(authUser) && (
              <Link to={routes.settings}>
                <div
                  className={cn(
                    "flex items-center gap-3 cursor-pointer px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 group",
                    {
                      "bg-gradient-to-r from-green-500 to-green-600 text-white transform scale-105":
                        location.pathname == routes.settings,
                      "text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700":
                        location.pathname != routes.settings,
                    }
                  )}
                >
                  <div
                    className={cn("text-xl transition-all duration-200", {
                      "text-white": location.pathname == routes.settings,
                      "text-gray-500 group-hover:text-green-600":
                        location.pathname != routes.settings,
                    })}
                  >
                    <FaCog />
                  </div>
                  <span
                    className={cn(
                      "transition-all duration-200",
                      openSideBar ? "block opacity-100" : "md:hidden opacity-0"
                    )}
                  >
                    {t("navigation.Settings")}
                  </span>
                </div>
              </Link>
            )}

            {authUser && (
              <Link to={routes.info}>
                <div
                  className={cn(
                    "flex items-center gap-3 cursor-pointer px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 group",
                    {
                      "bg-gradient-to-r from-orange-500 to-orange-600 text-white transform scale-105":
                        location.pathname == routes.info,
                      "text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 hover:text-orange-700":
                        location.pathname != routes.info,
                    }
                  )}
                >
                  <div
                    className={cn("text-xl transition-all duration-200", {
                      "text-white": location.pathname == routes.info,
                      "text-gray-500 group-hover:text-orange-600":
                        location.pathname != routes.info,
                    })}
                  >
                    <GoInfo />
                  </div>
                  <span
                    className={cn(
                      "transition-all duration-200",
                      openSideBar ? "block opacity-100" : "md:hidden opacity-0"
                    )}
                  >
                    {t("navigation.Help")}
                  </span>
                </div>
              </Link>
            )}
          </div>

          {/* Beta Badge */}
          <div className="mt-8 px-4">
            <div
              className={cn(
                "bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-semibold px-3 py-2 rounded-full flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-xl",
                openSideBar ? "flex-row" : "flex-col"
              )}
            >
              <RocketLaunchIcon fontSize="small" className="animate-pulse" />
              {openSideBar && <span>{t("general.betaLaunch")}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* User Profile Section */}
      <div className="p-4 border-t border-gray-200 bg-white/50 backdrop-blur-sm">
        <Button
          id="basic-button"
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          className="w-full items-center cursor-pointer p-3 flex justify-between rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all duration-200 hover:shadow-md"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
        >
          <div className="flex items-center gap-3">
            <Avatar
              sx={{
                bgcolor: "linear-gradient(45deg, #667eea 0%, #764ba2 100%)",
                width: 40,
                height: 40,
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              {authUser.nickName[0]}
            </Avatar>
            <div
              className={cn(
                "text-left transition-all duration-200",
                openSideBar ? "block opacity-100" : "hidden opacity-0"
              )}
            >
              <div className="font-semibold text-gray-900 text-sm">
                {authUser?.nickName}
              </div>
              <span className="text-xs text-gray-500">{authUser?.email}</span>
            </div>
          </div>
          <div
            className={cn(
              "transition-all duration-200",
              openSideBar ? "block opacity-100" : "hidden opacity-0"
            )}
          >
            {locale === LANG.AR ? (
              <MdArrowBackIos className="text-gray-400" />
            ) : (
              <MdArrowForwardIos className="text-gray-400" />
            )}
          </div>
        </Button>
      </div>

      {/* User Menu */}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              width: "220px",
              border: "1px solid rgba(0, 0, 0, 0.05)",
            },
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={handelLogout}
          className="hover:!bg-gradient-to-r hover:!from-red-50 hover:!to-pink-50 flex gap-3 hover:text-red-600 transition-all duration-200 py-3"
        >
          <LogOutIcon className="text-red-500" />
          {t("navigation.Logout")}
        </MenuItem>
        <Link to={routes.settings}>
          <MenuItem className="hover:!bg-gradient-to-r hover:!from-blue-50 hover:!to-purple-50 flex gap-3 hover:text-blue-600 transition-all duration-200 py-3">
            <User2Icon className="text-blue-500" />
            {t("navigation.MyProfile")}
          </MenuItem>
        </Link>
      </Menu>
    </div>
  );
}

export default LeftSide;
